import { useEffect, useRef, useState } from 'react'
import { Loader2, Trash2, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useDeleteHero, useUploadHero } from '@/features/projects/use-photos'
import { ProjectHeroCropDialog } from './project-hero-crop-dialog'

interface ProjectHeroBlockProps {
  projectId: string
  heroThumbUrl: string | null
}

/**
 * Phase 8 — Project Hero upload block on the project header settings panel. Drag-and-drop /
 * click-to-pick → 16:9 crop → upload mutation. Remove triggers the delete mutation. The thumb
 * URL is rendered as a preview; absent → empty-state dashed border.
 */
export const ProjectHeroBlock = ({ projectId, heroThumbUrl }: ProjectHeroBlockProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)

  const upload = useUploadHero(projectId)
  const remove = useDeleteHero(projectId)

  useEffect(() => {
    return () => {
      if (imageSrc) URL.revokeObjectURL(imageSrc)
    }
  }, [imageSrc])

  const handlePickedFile = (file: File | null | undefined) => {
    if (!file) return
    setImageSrc(URL.createObjectURL(file))
  }

  const onConfirmCrop = (blob: Blob) => {
    upload.mutate(blob, {
      onSuccess: () => {
        toast.success('Hero updated')
        setImageSrc(null)
      },
      onError: (err) => {
        const apiErr = err as { detail?: string; status?: number }
        toast.error(apiErr.detail ?? 'Could not upload hero')
      },
    })
  }

  const onRemove = () => {
    remove.mutate(undefined, {
      onSuccess: () => toast.success('Hero removed'),
      onError: () => toast.error('Could not remove hero'),
    })
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">Hero image</p>
      <div
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          handlePickedFile(e.dataTransfer.files?.[0])
        }}
        className={
          'space-y-3 rounded-md border border-dashed p-4 transition-colors ' +
          (dragOver ? 'border-primary bg-primary/5' : 'border-input bg-muted/20')
        }
      >
        {heroThumbUrl ? (
          <div className="aspect-video w-full overflow-hidden rounded-md border bg-muted">
            <img
              src={heroThumbUrl}
              alt="Project hero preview"
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
        ) : (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Drag and drop an image, or click &ldquo;Upload&rdquo;
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => {
              handlePickedFile(e.target.files?.[0])
              e.currentTarget.value = ''
            }}
          />
          <Button
            type="button"
            size="sm"
            className="gap-1"
            onClick={() => fileInputRef.current?.click()}
            disabled={upload.isPending}
          >
            {upload.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Upload
          </Button>
          {heroThumbUrl && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="gap-1"
              onClick={onRemove}
              disabled={remove.isPending}
            >
              <Trash2 className="h-4 w-4" />
              Remove
            </Button>
          )}
          <p className="text-xs text-muted-foreground">PNG, JPEG, or WebP up to 15 MB.</p>
        </div>
      </div>

      <ProjectHeroCropDialog
        open={imageSrc !== null}
        imageSrc={imageSrc}
        onCancel={() => setImageSrc(null)}
        onConfirm={onConfirmCrop}
        pending={upload.isPending}
      />
    </div>
  )
}
